// src/utils/SchemaComparator.ts
import { Competition, Solution, User } from '../models';

/**
 * CSVスキーマの比較と変更検出を行うユーティリティクラス
 */
export class SchemaComparator {
  /**
   * CSVヘッダーの変更を検出する
   * @param oldHeaders 古いCSVヘッダー
   * @param newHeaders 新しいCSVヘッダー
   * @returns 変更の詳細
   */
  static compareHeaders(oldHeaders: string[], newHeaders: string[]): SchemaChange {
    const addedColumns: string[] = [];
    const removedColumns: string[] = [];
    const renamedColumns: { oldName: string, newName: string }[] = [];
    
    // 追加されたカラムを検出
    for (const header of newHeaders) {
      if (!oldHeaders.includes(header)) {
        addedColumns.push(header);
      }
    }
    
    // 削除されたカラムを検出
    for (const header of oldHeaders) {
      if (!newHeaders.includes(header)) {
        removedColumns.push(header);
      }
    }
    
    // リネームされた可能性のあるカラムを検出（ヒューリスティック）
    if (addedColumns.length > 0 && removedColumns.length > 0) {
      // 単純な例: 名前が似ているカラムを検出
      for (const removed of removedColumns) {
        for (const added of addedColumns) {
          // 類似度が高い場合はリネームとみなす
          if (this.calculateStringSimilarity(removed, added) > 0.7) {
            renamedColumns.push({ oldName: removed, newName: added });
            // 処理済みのカラムをリストから削除
            addedColumns.splice(addedColumns.indexOf(added), 1);
            removedColumns.splice(removedColumns.indexOf(removed), 1);
            break;
          }
        }
      }
    }
    
    return {
      hasChanges: addedColumns.length > 0 || removedColumns.length > 0 || renamedColumns.length > 0,
      addedColumns,
      removedColumns,
      renamedColumns
    };
  }
  
  /**
   * コンペティションCSVスキーマの変更を検出する
   * @param oldHeaders 古いCSVヘッダー
   * @param newHeaders 新しいCSVヘッダー
   * @returns 変更の詳細
   */
  static detectCompetitionSchemaChanges(oldHeaders: string[], newHeaders: string[]): SchemaChange {
    const schemaChange = this.compareHeaders(oldHeaders, newHeaders);
    
    // 必須カラムの確認
    const requiredHeaders = ['Slug', 'Title', 'Subtitle', 'DeadlineDate'];
    const missingRequired = requiredHeaders.filter(h => !newHeaders.includes(h));
    
    if (missingRequired.length > 0) {
      schemaChange.hasCriticalChanges = true;
      schemaChange.criticalChanges = `必須カラムが欠けています: ${missingRequired.join(', ')}`;
    }
    
    return schemaChange;
  }
  
  /**
   * ソリューションCSVスキーマの変更を検出する
   * @param oldHeaders 古いCSVヘッダー
   * @param newHeaders 新しいCSVヘッダー
   * @returns 変更の詳細
   */
  static detectSolutionSchemaChanges(oldHeaders: string[], newHeaders: string[]): SchemaChange {
    const schemaChange = this.compareHeaders(oldHeaders, newHeaders);
    
    // 必須カラムの確認
    const requiredHeaders = ['CompetitionSlug', 'Rank', 'Score', 'UserIds', 'Summary'];
    const missingRequired = requiredHeaders.filter(h => !newHeaders.includes(h));
    
    if (missingRequired.length > 0) {
      schemaChange.hasCriticalChanges = true;
      schemaChange.criticalChanges = `必須カラムが欠けています: ${missingRequired.join(', ')}`;
    }
    
    return schemaChange;
  }
  
  /**
   * ユーザーCSVスキーマの変更を検出する
   * @param oldHeaders 古いCSVヘッダー
   * @param newHeaders 新しいCSVヘッダー
   * @returns 変更の詳細
   */
  static detectUserSchemaChanges(oldHeaders: string[], newHeaders: string[]): SchemaChange {
    const schemaChange = this.compareHeaders(oldHeaders, newHeaders);
    
    // 必須カラムの確認
    const requiredHeaders = ['UserId', 'CurrentRank', 'HighestRank'];
    const missingRequired = requiredHeaders.filter(h => !newHeaders.includes(h));
    
    if (missingRequired.length > 0) {
      schemaChange.hasCriticalChanges = true;
      schemaChange.criticalChanges = `必須カラムが欠けています: ${missingRequired.join(', ')}`;
    }
    
    return schemaChange;
  }
  
  /**
   * 文字列の類似度を計算する（レーベンシュタイン距離ベース）
   * @param str1 文字列1
   * @param str2 文字列2
   * @returns 類似度（0～1）
   */
  private static calculateStringSimilarity(str1: string, str2: string): number {
    const len1 = str1.length;
    const len2 = str2.length;
    
    // 長さが大きく異なる場合は類似度を低くする
    if (Math.abs(len1 - len2) > Math.min(len1, len2) * 0.5) {
      return 0;
    }
    
    // レーベンシュタイン距離の計算
    const dp: number[][] = Array(len1 + 1).fill(null).map(() => Array(len2 + 1).fill(0));
    
    for (let i = 0; i <= len1; i++) {
      dp[i][0] = i;
    }
    
    for (let j = 0; j <= len2; j++) {
      dp[0][j] = j;
    }
    
    for (let i = 1; i <= len1; i++) {
      for (let j = 1; j <= len2; j++) {
        const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,      // 削除
          dp[i][j - 1] + 1,      // 挿入
          dp[i - 1][j - 1] + cost // 置換
        );
      }
    }
    
    // 最大の編集距離は両方の文字列の長さの合計
    const maxDistance = Math.max(len1, len2);
    // 類似度 = 1 - (編集距離 / 最大距離)
    return maxDistance === 0 ? 1 : 1 - dp[len1][len2] / maxDistance;
  }
}

/**
 * スキーマ変更の詳細を表すインターフェース
 */
export interface SchemaChange {
  hasChanges: boolean;
  hasCriticalChanges?: boolean;
  criticalChanges?: string;
  addedColumns: string[];
  removedColumns: string[];
  renamedColumns: { oldName: string, newName: string }[];
}
